import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { dispatch } from '../../utils/event-helpers';
import { styles } from './asc-table.styles';
import { TableKeyboardController } from './table-keyboard.controller';
// Side-effect imports for composed elements
import '../../elements/asc-table-cell/asc-table-cell';

// ─── Type aliases ──────────────────────────────────────────────────────────

type ColumnAlign = 'left' | 'center' | 'right';
type SortDirection = 'asc' | 'desc';
type TableVariant = 'card' | 'flat';
type HeaderStyle = 'default' | 'brand';

export interface ColumnDef {
  /** Unique column key; must match a key in each row data object. */
  key: string;
  /** Human-readable column header label. Also used as the accessible name for sort buttons. */
  label: string;
  /** Optional rich HTML content rendered in the column header instead of the plain `label` text. When omitted, `label` is rendered as-is. */
  headerContent?: unknown;
  /** When `true`, the column header renders a sort affordance button. Default: `false`. */
  sortable?: boolean;
  /** CSS width value applied to the `<col>` element. If omitted, the column sizes naturally. */
  width?: string;
  /** Text and content alignment within column cells. Default: `'left'`. */
  align?: ColumnAlign;
}

export type RowData = Record<string, unknown>;

// ─── SVG icon helpers ──────────────────────────────────────────────────────

/** Chevron-selector-vertical icon — shown on sortable column headers. */
const ICON_CHEVRON_VERTICAL = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 12 12"
  width="12"
  height="12"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
>
  <path d="M3.5 4.5L6 2l2.5 2.5M3.5 7.5L6 10l2.5-2.5" />
</svg>`;

// ─── Component ─────────────────────────────────────────────────────────────

/**
 * A structured data table for displaying rows and columns of information.
 *
 * Supports externally controlled sorting, row selection, and pagination. The
 * table is fully presentational — all state transitions (sort, selection, page
 * change) are communicated outward via events; the implementer drives data
 * updates and controlled-binding properties in response.
 *
 * Keyboard navigation follows the ARIA Data Grid pattern: arrow keys move focus
 * between cells, `Tab` moves between interactive elements within the grid.
 *
 * @element asc-table
 *
 * @fires asc-sort-change - Fired when a sortable column header is clicked.
 *   Detail: `{ key: string; direction: 'asc' | 'desc' }`
 * @fires asc-selection-change - Fired when a row checkbox or the master
 *   select-all is toggled.
 *   Detail: `{ selectedIds: Array<string | number> }`
 * @fires asc-row-click - Fired when a row body (non-interactive area) is
 *   clicked.
 *   Detail: `{ row: RowData }`
 *
 * @slot title       - Short heading rendered in the top-left of the header area.
 *   Accepts `<h2>`, `<h3>`, or any heading element.
 * @slot description - Supporting subheading below the title. Accepts `<p>` or
 *   inline text.
 * @slot header-actions - Controls in the top-right of the header area. Accepts
 *   `<asc-button>` elements or other action controls.
 * @slot empty       - Content rendered when `rows` is empty and `loading` is
 *   false.
 *
 * @csspart base             - Root `<div>` — border, background, shadow, radius.
 * @csspart header           - Optional title/description/actions bar.
 * @csspart header-content   - Inner container for `title` and `description` slots.
 * @csspart header-actions   - Container for the `header-actions` slot.
 * @csspart table-container  - Horizontally scrollable wrapper around `<table>`.
 * @csspart table            - The native `<table>` element.
 * @csspart caption          - Visually hidden accessible caption element.
 * @csspart colgroup         - The `<colgroup>` for per-column widths.
 * @csspart thead            - The column header section.
 * @csspart header-row       - The single column header `<tr>`.
 * @csspart col-header       - Each column header `<th>`.
 * @csspart checkbox-col-header - Master select-all `<th>` (when `selectable`).
 * @csspart sort-button      - Interactive sort trigger inside a sortable `<th>`.
 * @csspart sort-icon        - Sort direction icon element within a sort button.
 * @csspart tbody            - The table body section.
 * @csspart row              - Each data `<tr>`.
 * @csspart checkbox-cell    - Leading row checkbox `<td>` (when `selectable`).
 * @csspart cell             - Each data `<td>` wrapper containing `<asc-table-cell>`.
 * @csspart empty-state      - Full-width container for the `empty` slot.
 * @csspart skeleton-row     - A loading skeleton `<tr>`.
 *
 * @cssprop --asc-table-bg                       - Wrapper background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-table-border-color             - Outer border color. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-table-border-radius            - Outer radius (`card` only). Default: `var(--asc-radius-lg)`
 * @cssprop --asc-table-shadow                   - Box shadow (`card` only). Default: `0px 1px 2px 0px var(--asc-shadow-xs)`
 * @cssprop --asc-table-header-bg               - Header area background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-table-header-padding-x        - Header horizontal padding. Default: `var(--asc-spacing-3xl)`
 * @cssprop --asc-table-header-padding-top      - Header top padding. Default: `var(--asc-spacing-2xl)`
 * @cssprop --asc-table-header-text-gap         - Gap between title and description. Default: `var(--asc-spacing-xxs)`
 * @cssprop --asc-table-header-actions-gap      - Gap between text group and actions. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-table-header-border-color     - Divider between header and table. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-table-title-color             - Title text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-table-title-font-size         - Title font size. Default: `var(--asc-font-size-text-lg)`
 * @cssprop --asc-table-title-font-weight       - Title font weight. Default: `var(--asc-font-weight-semibold)`
 * @cssprop --asc-table-title-line-height       - Title line height. Default: `var(--asc-line-height-text-lg)`
 * @cssprop --asc-table-description-color       - Description text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-table-description-font-size   - Description font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-table-description-font-weight - Description font weight. Default: `var(--asc-font-weight-regular)`
 * @cssprop --asc-table-col-header-bg           - Column header background. Default: `var(--asc-bg-secondary)` (card) / `var(--asc-bg-primary)` (flat)
 * @cssprop --asc-table-col-header-bg-active    - Column header background (active sort). Default: `var(--asc-bg-primary)`
 * @cssprop --asc-table-col-header-height       - Column header row height. Default: `44px`
 * @cssprop --asc-table-col-header-padding-x    - Column header horizontal padding. Default: `var(--asc-spacing-3xl)`
 * @cssprop --asc-table-col-header-padding-y    - Column header vertical padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-table-col-header-border-color - Column header bottom border (default). Default: `var(--asc-border-secondary)`
 * @cssprop --asc-table-col-header-border-color-active - Column header bottom border (active sort). Default: `var(--asc-border-brand)`
 * @cssprop --asc-table-col-header-color        - Column header label color. Default: `var(--asc-text-quaternary)`
 * @cssprop --asc-table-col-header-font-size    - Column header font size. Default: `var(--asc-font-size-text-xs)`
 * @cssprop --asc-table-col-header-font-weight  - Column header font weight. Default: `var(--asc-font-weight-semibold)`
 * @cssprop --asc-table-sort-icon-color         - Sort icon color (inactive). Default: `var(--asc-text-quaternary)`
 * @cssprop --asc-table-sort-icon-color-active  - Sort icon color (active). Default: `var(--asc-text-brand-tertiary)`
 * @cssprop --asc-table-sort-button-bg-hover    - Sort button hover background. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-table-sort-icon-gap           - Gap between column label and sort icon. Default: `var(--asc-spacing-xs)`
 * @cssprop --asc-table-checkbox-col-width      - Checkbox column width. Default: `44px`
 * @cssprop --asc-table-checkbox-size           - Checkbox size. Default: `20px`
 * @cssprop --asc-table-checkbox-border-color   - Checkbox border color. Default: `var(--asc-border-primary)`
 * @cssprop --asc-table-checkbox-border-radius  - Checkbox border radius. Default: `var(--asc-spacing-sm)`
 * @cssprop --asc-table-row-height              - Data row height. Default: `72px`
 * @cssprop --asc-table-row-border-color        - Row separator color (card). Default: `var(--asc-border-secondary)`
 * @cssprop --asc-table-row-border-color-light  - Row separator color (flat). Default: `var(--asc-border-tertiary)`
 * @cssprop --asc-table-skeleton-bg             - Skeleton cell base color. Default: `var(--asc-bg-secondary)`
 * @cssprop --asc-table-skeleton-shimmer-color  - Skeleton shimmer highlight. Default: `var(--asc-bg-tertiary)`
 */
@customElement('asc-table')
export class AscTable extends LitElement {
  static override styles = [styles];

  // ── Public API ────────────────────────────────────────────────────────────

  /** Column definition objects. Each must have a unique `key` and a `label`. */
  @property({ type: Array }) columns: ColumnDef[] = [];

  /** Row data objects. Keys must match `ColumnDef.key` values. */
  @property({ type: Array }) rows: RowData[] = [];

  /**
   * `card` — full border with `border-radius` and drop shadow (standalone table).
   * `flat` — top + bottom borders only, no radius, no shadow (embedded in layout).
   */
  @property({ type: String, reflect: true }) variant: TableVariant = 'card';

  /** When `true`, renders a leading checkbox per row and a master select-all in the header. */
  @property({ type: Boolean, reflect: true }) selectable = false;

  /** Controlled. Array of currently selected row IDs (resolved via `rowIdKey`). */
  @property({ type: Array }) selectedRows: Array<string | number> = [];

  /** Required when `selectable` is `true`. Key within each row object that uniquely identifies it. */
  @property({ type: String, attribute: 'row-id-key' }) rowIdKey = '';

  /** Controlled. The column `key` currently being sorted. Empty string = no active sort. */
  @property({ type: String, attribute: 'sort-key' }) sortKey = '';

  /** Controlled. Direction of the active sort. */
  @property({ type: String, reflect: true, attribute: 'sort-direction' })
  sortDirection: SortDirection = 'asc';

  /** When `true`, renders skeleton rows in place of row data and sets `aria-busy` on the table. */
  @property({ type: Boolean, reflect: true }) loading = false;

  /** When `true`, suppresses all row interactions (selection, sorting, row-click). */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /**
   * `default` — gray background column headers (matches `--asc-bg-secondary`).
   * `brand` — white background with a brand-colored bottom border.
   */
  @property({ type: String, reflect: true, attribute: 'header-style' })
  headerStyle: HeaderStyle = 'default';

  /** When `true`, hides the column header row (`<thead>`). Data rows display without column labels. */
  @property({ type: Boolean, reflect: true, attribute: 'hide-header' }) hideHeader = false;

  /**
   * Accessible name for the table, announced by screen readers via a visually
   * hidden `<caption>`. Not user-facing visible content.
   */
  @property({ type: String }) caption = '';

  /** Number of skeleton rows shown during loading. */
  @property({ type: Number, attribute: 'skeleton-rows' }) skeletonRows = 5;

  // ── Internal state ────────────────────────────────────────────────────────

  @state() private _hasHeader = false;

  // ── DOM references ────────────────────────────────────────────────────────

  @query('[part="table"]') private _tableEl!: HTMLTableElement;
  /**
   * Direct reference to the master select-all checkbox so we can force-set
   * `indeterminate` imperatively. Lit's dirty-check skips re-applying a
   * property binding whose computed value hasn't changed, but the browser
   * resets `indeterminate` to `false` whenever the element is clicked — so we
   * use both the template binding (initial state) and an imperative write in
   * `updated()` (to handle browser resets after clicks).
   */
  @query('[part="checkbox-col-header"] input.checkbox')
  private _masterCheckboxEl?: HTMLInputElement;

  @query('slot[name="title"]') private _titleSlot?: HTMLSlotElement;
  @query('slot[name="description"]') private _descSlot?: HTMLSlotElement;
  @query('slot[name="header-actions"]') private _actionsSlot?: HTMLSlotElement;

  // ── Controllers ───────────────────────────────────────────────────────────

  private readonly _keyboard = new TableKeyboardController(this);

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override updated(changed: PropertyValues<this>): void {
    if (
      changed.has('rows') ||
      changed.has('loading') ||
      changed.has('selectable') ||
      changed.has('hideHeader') ||
      changed.has('columns')
    ) {
      // Re-register table reference so controller rebuilds the grid after
      // any structural change (new rows, loading toggles, header visibility).
      if (this._tableEl) this._keyboard.setTable(this._tableEl);
    }
    // Force-set the master checkbox's `indeterminate` property after every
    // render that could affect selection state. Only counts IDs from the
    // current visible page (intersection with selected) so the header checkbox
    // correctly reflects the visible slice, not cross-page selections.
    if (this.selectable && this._masterCheckboxEl) {
      const visibleRowIds = this.rowIdKey
        ? new Set(this.rows.map(r => r[this.rowIdKey] as string | number))
        : new Set<string | number>();
      const visibleSelectedCount = this.rowIdKey
        ? this.selectedRows.filter(id => visibleRowIds.has(id)).length
        : 0;
      const allSelected = this.rows.length > 0 && visibleSelectedCount === this.rows.length;
      this._masterCheckboxEl.indeterminate = visibleSelectedCount > 0 && !allSelected;
    }
  }

  // ── Private handlers ──────────────────────────────────────────────────────

  private _handleHeaderSlotChange(): void {
    const hasTitle = (this._titleSlot?.assignedNodes({ flatten: true }).length ?? 0) > 0;
    const hasDesc = (this._descSlot?.assignedNodes({ flatten: true }).length ?? 0) > 0;
    const hasActions = (this._actionsSlot?.assignedNodes({ flatten: true }).length ?? 0) > 0;
    this._hasHeader = hasTitle || hasDesc || hasActions;
  }

  private _handleSortClick(col: ColumnDef): void {
    if (this.disabled || !col.sortable) return;
    const direction: SortDirection =
      this.sortKey === col.key && this.sortDirection === 'asc' ? 'desc' : 'asc';
    dispatch<{ key: string; direction: SortDirection }>(this, 'asc-sort-change', {
      detail: { key: col.key, direction },
    });
  }

  private _handleRowCheckbox(rowId: string | number, checked: boolean): void {
    if (this.disabled) return;
    const next = checked
      ? [...this.selectedRows, rowId]
      : this.selectedRows.filter(id => id !== rowId);
    dispatch<{ selectedIds: Array<string | number> }>(this, 'asc-selection-change', {
      detail: { selectedIds: next },
    });
  }

  private _handleSelectAll(checked: boolean): void {
    if (this.disabled) return;
    const ids: Array<string | number> = checked
      ? this.rows.map(r => r[this.rowIdKey] as string | number)
      : [];
    dispatch<{ selectedIds: Array<string | number> }>(this, 'asc-selection-change', {
      detail: { selectedIds: ids },
    });
  }

  private _handleRowClick(e: MouseEvent, row: RowData): void {
    if (this.disabled) return;
    const target = e.target as HTMLElement;
    // Exclude native inputs/buttons, asc-button custom elements, and anchor links
    if (target.closest('input[type="checkbox"], button, asc-button, a')) return;
    dispatch<{ row: RowData }>(this, 'asc-row-click', { detail: { row } });
  }

  // ── Sub-renderers ─────────────────────────────────────────────────────────

  private _renderColHeader(col: ColumnDef) {
    const isActive = this.sortKey === col.key;
    const ariaSort = isActive
      ? this.sortDirection === 'asc'
        ? 'ascending'
        : 'descending'
      : ('none' as const);

    const sortIcon = ICON_CHEVRON_VERTICAL;

    return html`
      <th
        part="col-header"
        role="columnheader"
        scope="col"
        tabindex="-1"
        aria-sort=${col.sortable ? ariaSort : nothing}
      >
        ${col.sortable
          ? html`
              <button
                type="button"
                part="sort-button"
                aria-label="${col.label}, activate to sort ${isActive && this.sortDirection === 'asc' ? 'descending' : 'ascending'}"
                ?disabled=${this.disabled}
                aria-disabled=${this.disabled ? 'true' : nothing}
                @click=${() => this._handleSortClick(col)}
              >
                ${col.headerContent ?? col.label}
                <span part="sort-icon" aria-hidden="true">${sortIcon}</span>
              </button>
            `
          : (col.headerContent ?? col.label)}
      </th>
    `;
  }

  private _renderRow(row: RowData) {
    const rawId = this.rowIdKey ? row[this.rowIdKey] : undefined;
    if (this.selectable && (rawId === undefined || rawId === null)) {
      console.warn('[asc-table] selectable=true requires "rowIdKey" to be set and each row must contain that key. Row selection will be skipped for rows with missing IDs.');
    }
    const rowId = rawId as string | number | undefined;
    const isSelected = this.selectable && rowId != null && this.selectedRows.includes(rowId);

    return html`
      <tr
        part="row"
        role="row"
        aria-selected=${this.selectable ? String(isSelected) : nothing}
        @click=${(e: MouseEvent) => this._handleRowClick(e, row)}
      >
        ${this.selectable
          ? html`
              <td part="checkbox-cell" role="gridcell" tabindex="-1">
                <asc-table-cell
                  ?selected=${isSelected}
                  ?disabled=${this.disabled}
                >
                  <input
                    class="checkbox"
                    type="checkbox"
                    aria-label="Select row"
                    .checked=${isSelected}
                    ?disabled=${this.disabled}
                    aria-disabled=${this.disabled ? 'true' : nothing}
                    @change=${rowId != null ? (e: Event) =>
                      this._handleRowCheckbox(rowId, (e.target as HTMLInputElement).checked) : nothing}
                    @click=${(e: MouseEvent) => e.stopPropagation()}
                  />
                </asc-table-cell>
              </td>
            `
          : nothing}
        ${this.columns.map(
          col => html`
            <td
              part="cell"
              role="gridcell"
              tabindex="-1"
              style=${ifDefined(col.align ? `text-align: ${col.align}` : undefined)}
            >
              <asc-table-cell
                ?selected=${isSelected}
                ?disabled=${this.disabled}
              >
                ${row[col.key] ?? nothing}
              </asc-table-cell>
            </td>
          `,
        )}
      </tr>
    `;
  }

  private _renderSkeletonRows(colSpan: number) {
    return repeat(
      Array.from({ length: this.skeletonRows }),
      (_, i) => i,
      (_, i) => html`
        <tr part="skeleton-row" role="row" aria-hidden="true">
          ${Array.from(
            { length: colSpan },
            () => html`
              <td role="gridcell">
                <asc-table-cell>
                  <span
                    class="skeleton-cell"
                    style="width: ${60 + (i % 3) * 10}%"
                  ></span>
                </asc-table-cell>
              </td>
            `,
          )}
        </tr>
      `,
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  override render() {
    // Compute selection state from only the IDs visible in the current page
    // slice so the header checkbox correctly reflects the visible set even when
    // selectedRows contains IDs from other pages.
    const visibleRowIds = this.rowIdKey
      ? new Set(this.rows.map(r => r[this.rowIdKey] as string | number))
      : new Set<string | number>();
    const visibleSelectedCount = this.rowIdKey
      ? this.selectedRows.filter(id => visibleRowIds.has(id)).length
      : 0;
    const allSelected = this.rows.length > 0 && visibleSelectedCount === this.rows.length;
    const someSelected = visibleSelectedCount > 0 && !allSelected;
    const colSpan = this.columns.length + (this.selectable ? 1 : 0);
    const captionText = this.loading
      ? 'Loading table data'
      : (this.caption || nothing);

    return html`
      <div part="base">
        <!-- ─ Header area (conditional) ───────────────────────── -->
        ${this._hasHeader
          ? html`
              <div part="header">
                <div part="header-content">
                  <slot
                    name="title"
                    @slotchange=${this._handleHeaderSlotChange}
                  ></slot>
                  <slot
                    name="description"
                    @slotchange=${this._handleHeaderSlotChange}
                  ></slot>
                </div>
                <div part="header-actions">
                  <slot
                    name="header-actions"
                    @slotchange=${this._handleHeaderSlotChange}
                  ></slot>
                </div>
              </div>
            `
          : html`
              <!-- Hidden sentinel slots — detect when header content is assigned -->
              <slot
                name="title"
                hidden
                @slotchange=${this._handleHeaderSlotChange}
              ></slot>
              <slot
                name="description"
                hidden
                @slotchange=${this._handleHeaderSlotChange}
              ></slot>
              <slot
                name="header-actions"
                hidden
                @slotchange=${this._handleHeaderSlotChange}
              ></slot>
            `}

        <!-- ─ Scrollable table container ──────────────────────── -->
        <div part="table-container">
          <table
            part="table"
            role="grid"
            aria-busy=${this.loading ? 'true' : 'false'}
            aria-label=${ifDefined(this.caption || undefined)}
          >
            <caption part="caption">${captionText}</caption>

            <colgroup part="colgroup">
              ${this.selectable
                ? html`<col
                    style="width: var(--asc-table-checkbox-col-width, 44px)"
                  />`
                : nothing}
              ${this.columns.map(
                col =>
                  html`<col
                    style=${ifDefined(col.width ? `width: ${col.width}` : undefined)}
                  />`,
              )}
            </colgroup>

            ${this.hideHeader
              ? nothing
              : html`
                <thead part="thead">
                  <tr part="header-row" role="row">
                    ${this.selectable
                      ? html`
                          <th
                            part="checkbox-col-header"
                            role="columnheader"
                            scope="col"
                            tabindex="-1"
                          >
                            <input
                              class="checkbox"
                              type="checkbox"
                              aria-label="Select all rows"
                              .checked=${allSelected}
                              .indeterminate=${someSelected}
                              ?disabled=${this.disabled}
                              aria-disabled=${this.disabled ? 'true' : nothing}
                              @change=${(e: Event) =>
                                this._handleSelectAll(
                                  (e.target as HTMLInputElement).checked,
                                )}
                            />
                          </th>
                        `
                      : nothing}
                    ${this.columns.map(col => this._renderColHeader(col))}
                  </tr>
                </thead>
              `}

            <tbody part="tbody">
              ${this.loading
                ? this._renderSkeletonRows(colSpan)
                : this.rows.length === 0
                  ? html`
                      <tr part="empty-state" role="row">
                        <td colspan=${colSpan} role="gridcell">
                          <slot name="empty"></slot>
                        </td>
                      </tr>
                    `
                  : repeat(
                      this.rows,
                      row =>
                        this.rowIdKey && row[this.rowIdKey] != null
                          ? row[this.rowIdKey]
                          : row,
                      row => this._renderRow(row),
                    )}
            </tbody>
          </table>
        </div>

      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-table': AscTable;
  }
}
